import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DocumentsList, { SortEvent, SortOrder, CustomColDef } from '../../components/DocumentsList/DocumentsList';
import { useGetDocumentsFromSearchApi } from '../../shared/queries/useGetDocumentsFromSearchApi';
import { DocumentData, DocumentResponse, SearchDocumentRequest } from '../../shared/types/Document';
import {
  DocumentPrintingPagePlaceholder,
  DocumentsDownloadType,
  SUPPORTED_PRINT_FILE_TYPES,
} from 'src/utils/constants/common';
import { TrashIcon, CaretDropdownIcon } from '@ds-icons';
import editButton from '@icons/note-pencil.svg';
import NoDocumentsIcon from '@icons/no-documents-found.svg';
import NoDocumentsDeloitteIcon from '@icons/deloitte/no-documents-found.svg';
import { useGetUser } from 'src/shared/queries/useGetUser';
import classNames from 'classnames';
import Paginator from 'src/components/Paginator/Paginator';
import { IOption } from '@ds/elements/Select/Select.types';
import { Button, DeleteConfirmationModal, Input, Tag, Toast, Tooltip } from '@ds';
import magnifyingGlass from '@icons/magnifying-glass.svg';
import RefreshIcon from '@icons/refresh.svg';
import PrintIcon from '@icons/printer.svg';
import DownloadIcon from '@icons/download.svg';
import { getFileExtension, paginatorDropdownOptions } from 'src/utils/CommonUtils';
import { IToastr } from '@ds/elements/Toast/Toast.types';
import { useDeleteDocument } from 'src/shared/mutations/useDeleteDocument';
import { getDocumentData } from 'src/shared/services/document-api/Documents';
import { FileUtil } from 'src/utils/FileUtils';
import { TooltipPosition, TooltipType } from '@ds/elements/Tooltip/Tooltip.types';
import UpdateDocumentModal from 'src/components/UpdateDocumentModal/UpdateDocumentModal';
import UploadI94DocumentsModal from 'src/components/UploadI94DocumentsModal/UploadI94DocumentsModal';
import { updateDocument } from 'src/shared/services/ims-api/Cases';
import {
  getFormattedDateFromUnix,
  compareUnixDates,
  getPreferredDateFormat,
  DateFormat,
} from 'src/utils/formatters/formatDate';
import { useThemeStore } from 'src/stores/ThemeStore';
import RemoteDocsBanner from 'src/components/RemoteDocsBanner/RemoteDocsBanner';
import { GlobalCaseTransitionModalType } from 'src/components/GlobalCaseTransitionModal/GlobalCaseTransitionModal';
import { useGetAllActiveCases } from 'src/shared/queries/useGetAllActiveCases';
import styles from './Documents.module.scss';
import UserIcon from 'src/components/UserIcon/UserIcon';
import { getUserIcon } from 'src/utils/services/User';
import { ICaseInfo } from 'src/shared/types/Case';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { CaseDocumentsTabs } from 'src/utils/constants/Case';
import { TagVariant } from '@ds/elements/Tag/Tag.types';
import I94Banner from 'src/components/I94Banner/I94Banner';
import { useGetImmigrationDocDrafts } from 'src/shared/queries/useGetImmigrationDocDrafts';
import { useCheckHasActiveUsCase } from 'src/shared/queries/useCheckHasActiveUsCase';

const DEFAULT_PARAMS = {
  fetchWithApi: true,
  includeProfileDoc: false,
  pageSize: 10,
  pageNumber: 1,
  searchTerm: '',
  sortOrder: SortOrder.Descending,
};

const TooltipContent = {
  ForbiddenDelete: `You cannot delete or edit documents uploaded by another user`,
  ForbiddenPrint: `Only the following file types are supported for printing: ${SUPPORTED_PRINT_FILE_TYPES.join(', ')}`,
};

const CaseItem = (props: { caseInfo: ICaseInfo; onCaseClick: () => void }) => {
  const { caseInfo, onCaseClick } = props;

  return (
    <div className={styles['upload-dropdown-item']} onClick={onCaseClick}>
      <UserIcon userIcon={getUserIcon(caseInfo?.dependents, caseInfo?.isDependentOnly as boolean)} />
      <div className={styles['case-details']}>
        <span className={styles['case-name']}>{caseInfo.caseName}</span>
        <span className={styles['bal-number']}>({caseInfo.balNumber})</span>
      </div>
      {caseInfo.isNew && (
        <Tag className={styles['new-case-tag']} variant={TagVariant.Success}>
          NEW CASE
        </Tag>
      )}
    </div>
  );
};

const Documents: React.FC = () => {
  const { isDeloitteTheme } = useThemeStore();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const { data: userInfo } = useGetUser();
  const currentUserId = userInfo?.userId;
  const beneId = userInfo?.beneId;
  const [searchParams, setSearchParams] = useState<SearchDocumentRequest>({
    ...DEFAULT_PARAMS,
    ...{ beneId },
  });
  const [selectedDocuments, setSelectedDocuments] = useState<DocumentResponse[]>([]);
  const { data: activeCases, isLoading: isActiveCasesLoading } = useGetAllActiveCases(true, true);
  const { data: immigrationDocDrafts, refetch: refetchImmigrationDocsDraft } = useGetImmigrationDocDrafts(
    beneId as number
  );
  const { data: hasActiveUsCase } = useCheckHasActiveUsCase(beneId);
  const { data, isFetching, refetch, isRefetching } = useGetDocumentsFromSearchApi(searchParams);
  const { mutateAsync: deleteDocument } = useDeleteDocument();

  const draftDocIds = useMemo(() => {
    if (!immigrationDocDrafts?.length) return new Set<string>();
    return new Set(immigrationDocDrafts.map((draft) => String(draft.docId)));
  }, [immigrationDocDrafts]);

  const documents = useMemo(() => {
    const results = data?.results || [];
    return results.map((doc) => ({ ...doc, isImmigrationDocDraft: draftDocIds.has(doc.id) }));
  }, [data?.results, draftDocIds]);

  const totalRows = data?.total || 0;
  const noDocuments = documents.length === 0 && !isFetching;
  const pageNumber = searchParams.pageNumber || 0;
  const isHavingSelectedDocuments = selectedDocuments.length > 0;
  const [currentSort, setCurrentSort] = useState<{ field?: string; order: SortOrder }>({
    field: undefined,
    order: SortOrder.Descending,
  });
  const [searchText, setSearchText] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHavingUnsupportedPrintFormat, setIsHavingUnsupportedPrintFormat] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [isSelectingOtherUsersUploadedDocs, setIsSelectingOtherUsersUploadedDocs] = useState(false);
  const [updatingDocument, setUpdatingDocument] = useState<DocumentResponse | null>(null);
  const [isUploadI94ModalOpen, setIsUploadI94ModalOpen] = useState(false);
  const isLoading = isFetching || isRefetching;
  const toastRef = useRef<IToastr>(null);
  const uploadDropdownRef = useRef<HTMLDivElement>(null);
  const [isUploadDropdownOpen, setIsUploadDropdownOpen] = useState(false);
  const UploadedDateFormat = `${
    getPreferredDateFormat(userInfo?.userSettings?.dateFormat as DateFormat).display
  } h:mma`;
  const { navigate } = useNavigateWithSecureParams();

  // Auto-open Upload I-94 modal if query param is present
  useEffect(() => {
    if (urlSearchParams.get('uploadI94') === 'true') {
      setIsUploadI94ModalOpen(true);

      // Remove the query param after opening the modal
      const updatedParams = new URLSearchParams(urlSearchParams);
      updatedParams.delete('uploadI94');
      setUrlSearchParams(updatedParams, { replace: true });
    }
  }, [urlSearchParams]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (uploadDropdownRef.current && !uploadDropdownRef.current.contains(event.target as Node)) {
        setIsUploadDropdownOpen(false);
      }
    };

    if (isUploadDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUploadDropdownOpen]);

  const openUpdateDocumentModal = (document: DocumentResponse) => {
    setUpdatingDocument(document);
  };

  const closeUpdateDocumentModal = () => {
    setUpdatingDocument(null);
  };

  const showDeleteConfirmationModal = () => {
    if (shouldDisableDeleteActionButton) {
      return;
    }
    setIsDeleteConfirmationModalOpen(true);
  };

  const hideDeleteConfirmationModal = () => {
    setIsDeleteConfirmationModalOpen(false);
  };

  const validateSelectedDocuments = (selectedDocuments: DocumentResponse[]) => {
    const isSelectingOtherUsersSelectedDocuments = selectedDocuments.some(
      (document) => currentUserId !== document.createdBy
    );
    setIsSelectingOtherUsersUploadedDocs(isSelectingOtherUsersSelectedDocuments);
  };

  const handleSortChanged = (event: SortEvent | null) => {
    if (event) {
      setCurrentSort({
        field: event.orderBy,
        order: event.sortOrder,
      });

      setSearchParams((prev) => ({
        ...prev,
        orderBy: event?.orderBy,
        sortOrder: event?.sortOrder ?? SortOrder.Descending,
      }));
    } else {
      setCurrentSort({
        field: undefined,
        order: SortOrder.Descending,
      });

      setSearchParams((prev) => ({
        ...prev,
        orderBy: undefined,
        sortOrder: SortOrder.Descending,
      }));
    }
  };

  const startToSearch = () => {
    setSearchParams((prev) => ({ ...prev, searchTerm: searchText }));
  };

  const handleKeyDownForSeachField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      startToSearch();
    }
  };

  const handleSelectedDocumentsChanged = (currentSelectedDocuments: DocumentResponse[]) => {
    setSelectedDocuments(currentSelectedDocuments);
    validateSelectedDocuments(currentSelectedDocuments);
    setIsHavingUnsupportedPrintFormat(
      !currentSelectedDocuments.every((document) =>
        document.files?.every((fileName) => SUPPORTED_PRINT_FILE_TYPES.includes(getFileExtension(fileName)))
      )
    );
  };

  const handleSelectRowsPerPageDropdown = (option: IOption) => {
    const value = option.value as number;
    if (value * pageNumber > totalRows) {
      const lastPage = Math.floor(totalRows / value);
      setSearchParams((prev) => ({ ...prev, pageNumber: lastPage }));
    }
    setSearchParams((prev) => ({ ...prev, pageSize: value }));
  };

  const handleChangePageInput = (option: IOption | null) => {
    const value = option?.value as number;
    setSearchParams((prev) => ({ ...prev, pageNumber: value }));
  };

  const refetchDocuments = async () => {
    await Promise.all([refetch(), refetchImmigrationDocsDraft()]);
  };

  const getDefaultObjForProcessingDocuments = () => {
    const documentKeys = selectedDocuments.map((document) => document.documentApiKey);
    return { documentKeys, userId: currentUserId, beneId };
  };

  const printDocuments = async () => {
    if (shouldDisablePrintActionButton) {
      return;
    }
    try {
      toastRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });
      setIsProcessing(true);
      const obj = { downloadType: DocumentsDownloadType.Print, ...getDefaultObjForProcessingDocuments(), toastRef };
      const fileDataPromise = getDocumentData(obj);
      await FileUtil.previewArrayBuffer(fileDataPromise, DocumentPrintingPagePlaceholder);
      toastRef.current?.clear();
    } finally {
      setIsProcessing(false);
    }
  };

  const removeDocument = async (document: DocumentResponse) => {
    await deleteDocument({ beneId: document.beneficiaryId, documentId: document.documentApiKey });
  };

  const deleteDocuments = async () => {
    const documentText = selectedDocuments.length > 1 ? 'documents' : 'document';
    try {
      hideDeleteConfirmationModal();
      setIsProcessing(true);
      toastRef.current?.show({
        message: `Deleting ${documentText}...`,
        severity: 'info',
        sticky: true,
      });
      await Promise.all(selectedDocuments.map((document) => removeDocument(document)));
      await refetchDocuments();
      toastRef.current?.clear();
      toastRef.current?.show({
        message: `Successfully deleted ${documentText}`,
        severity: 'success',
      });
    } catch (error) {
      toastRef.current?.clear();
      toastRef.current?.show({
        message: `Failed to delete ${documentText}`,
        severity: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const donwloadDocuments = async () => {
    if (shouldDisableDownloadActionButton) {
      return;
    }
    setIsProcessing(true);
    toastRef.current?.show({ message: 'Processing...', severity: 'info', sticky: true });
    try {
      const obj = { downloadType: DocumentsDownloadType.Default, ...getDefaultObjForProcessingDocuments(), toastRef };
      const { arrayBuffer, fileName, type } = await getDocumentData(obj);
      FileUtil.downloadArrayBuffer(arrayBuffer, fileName, type);
      toastRef.current?.clear();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClickUpdate = async (updatedDocument: DocumentData) => {
    try {
      setIsProcessing(true);
      closeUpdateDocumentModal();
      toastRef.current?.show({ message: 'Updating Document...', severity: 'info', sticky: true });
      await updateDocument(updatedDocument);
      setTimeout(() => {
        refetchDocuments();
      }, 1000);
      toastRef.current?.clear();
      toastRef.current?.show({
        message: 'Successfully Updated Document',
        severity: 'success',
      });
    } catch (error) {
      toastRef.current?.clear();
      toastRef.current?.show({
        message: `Failed to Update Document`,
        severity: 'error',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const onCaseClick = (caseInfo: ICaseInfo) => {
    if (caseInfo.isAidi) {
      navigate(`/home`, {
        secureParams: { caseId: caseInfo?.caseId },
      });
    } else {
      navigate(`/case-documents`, {
        secureParams: {
          caseId: caseInfo?.caseId,
          tab: CaseDocumentsTabs.CaseDocuments,
        },
      });
    }
  };

  const ActionsColumn = ({ data }: { data: DocumentResponse }) => {
    const document = data;
    const shouldRenderActions = currentUserId === document.createdBy;

    if (!shouldRenderActions) {
      return null;
    }

    return (
      <div>
        <img
          src={editButton}
          className={classNames(styles['doc-action-icon'], styles['edit-doc-icon'], {
            [styles['doc-action-icon--disabled']]: document.isImmigrationDocDraft,
          })}
          onClick={() => !document.isImmigrationDocDraft && openUpdateDocumentModal(document)}
        />
        <img
          src={TrashIcon}
          className={styles['doc-action-icon']}
          onClick={() => setIsDeleteConfirmationModalOpen(true)}
        ></img>
      </div>
    );
  };

  const COL_DEFS: CustomColDef[] = [
    {
      field: 'documentName',
      headerName: 'Name',
      flex: 1,
      tooltipField: 'documentName',
      customSortable: true,
    },
    {
      field: 'documentType',
      headerName: 'Document Type',
      flex: 1,
      customSortable: true,
    },
    {
      field: 'createdByName',
      headerName: 'Uploaded by',
      flex: 1,
      customSortable: true,
    },
    {
      field: 'createdEpochTime',
      headerName: 'Date Uploaded',
      flex: 1,
      valueGetter: (params) => getFormattedDateFromUnix(params.data.createdEpochTime, UploadedDateFormat),
      comparator: (date1, date2) => compareUnixDates(date1, date2),
      customSortable: true,
    },
    {
      field: 'actions',
      headerName: '',
      cellRenderer: ActionsColumn,
      customSortable: false,
    },
  ];

  const shouldDisableActionButton = !selectedDocuments.length || isLoading || isProcessing;
  const shouldDisableDeleteActionButton = shouldDisableActionButton || isSelectingOtherUsersUploadedDocs;
  const shouldDisablePrintActionButton = shouldDisableActionButton || isHavingUnsupportedPrintFormat;
  const shouldDisableDownloadActionButton = shouldDisableActionButton;

  return (
    <div className={styles['documents-page']}>
      <div className={styles['header-container']}>
        <h2>All Documents</h2>
        <I94Banner displayUploadButton={false} beneId={beneId} />
      </div>
      <div className={styles['actions-bar-container']}>
        <div className={styles['documents-search']}>
          <Input
            label="Search"
            placeholder="Search documents by name and type"
            value={searchText}
            icon={<img src={magnifyingGlass} className={styles['search-icon']} onClick={startToSearch} />}
            onKeyDown={handleKeyDownForSeachField}
            onBlur={startToSearch}
            onChange={(event) => setSearchText(event.target.value)}
          />
        </div>
        <div className={styles['right-section']}>
          {isHavingSelectedDocuments && (
            <div className={styles['msg-banner']}>
              {'You can either '} <span className={styles['highlight']}>print or download</span> the selected
              documents.{' '}
            </div>
          )}

          <div className={styles['upload-actions-container']}>
            {hasActiveUsCase && (
              <div className={styles['upload-i94']}>
                <Button onClick={() => setIsUploadI94ModalOpen(true)}>
                  <div className={styles['upload-arrow-up-icon']}></div>
                  <span className={styles['upload-to-case-text']}>NEW I-94</span>
                </Button>
              </div>
            )}
            <div className={styles['upload-documents']} ref={uploadDropdownRef}>
              <Button
                disabled={isActiveCasesLoading || !activeCases?.length}
                onClick={() => setIsUploadDropdownOpen((prev) => !prev)}
              >
                <div className={styles['upload-icon']}></div>
                <span className={styles['upload-to-case-text']}>UPLOAD TO A CASE</span>
                <img
                  src={CaretDropdownIcon}
                  className={classNames(styles['caret-dropdown-icon'], { [styles['rotated']]: isUploadDropdownOpen })}
                />
              </Button>
              {isUploadDropdownOpen && (
                <div className={styles['upload-dropdown']}>
                  <span className={styles['upload-dropdown-title']}>CASES</span>
                  <div className={styles['upload-dropdown-list']}>
                    {activeCases?.map((caseInfo) => (
                      <CaseItem key={caseInfo.caseId} caseInfo={caseInfo} onCaseClick={() => onCaseClick(caseInfo)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={styles['icons-container']}>
            <img className={classNames(styles['action-icon'])} src={RefreshIcon} onClick={refetchDocuments} />
            <img
              className={classNames('print-icon', styles['action-icon'], {
                [styles['disabled']]: shouldDisablePrintActionButton,
              })}
              src={PrintIcon}
              onClick={printDocuments}
            />
            {isHavingUnsupportedPrintFormat && (
              <Tooltip
                target=".print-icon"
                position={TooltipPosition.Left}
                content={TooltipContent.ForbiddenPrint}
                type={TooltipType.Error}
              />
            )}
            <img
              className={classNames(styles['action-icon'], { [styles['disabled']]: shouldDisableDownloadActionButton })}
              src={DownloadIcon}
              onClick={donwloadDocuments}
            />

            <img
              src={TrashIcon}
              className={classNames('download-icon', styles['action-icon'], {
                [styles['disabled']]: shouldDisableDeleteActionButton,
              })}
              onClick={showDeleteConfirmationModal}
            />
            {isSelectingOtherUsersUploadedDocs && (
              <Tooltip
                target=".download-icon"
                position={TooltipPosition.Left}
                content={TooltipContent.ForbiddenDelete}
                type={TooltipType.Error}
              />
            )}
          </div>
        </div>
      </div>

      {noDocuments ? (
        <div className={styles['no-documents-container']}>
          <img
            src={isDeloitteTheme ? NoDocumentsDeloitteIcon : NoDocumentsIcon}
            className={styles['no-documents-icon']}
            alt="No documents found"
          />
          {!searchText && <div className={styles['no-documents-text']}>No Documents here yet</div>}
        </div>
      ) : (
        <div>
          <RemoteDocsBanner
            pathname="documents"
            classNames="mt-4 -mb-2"
            type={GlobalCaseTransitionModalType.DOCUMENTS}
          />

          <DocumentsList
            colDefs={COL_DEFS}
            rowsData={documents}
            containerClassName={styles['all-documents-list-container']}
            isLoading={isLoading}
            onSelectionChanged={handleSelectedDocumentsChanged}
            onSortChanged={handleSortChanged}
            currentSortField={currentSort.field}
            currentSortOrder={currentSort.order}
          />
          <Paginator
            totalRows={totalRows}
            rowsPerPage={searchParams.pageSize}
            selectedPage={searchParams.pageNumber || 0}
            dropdownOptions={paginatorDropdownOptions}
            onSelectRowsPerPageDropdown={handleSelectRowsPerPageDropdown}
            onChangePageInput={handleChangePageInput}
          />
        </div>
      )}
      <Toast ref={toastRef} />
      <DeleteConfirmationModal
        title="DELETE DOCUMENTS?"
        confirmMessage="These documents will be deleted from all cases. Are you sure you want to proceed?"
        onCancel={hideDeleteConfirmationModal}
        onConfirm={deleteDocuments}
        visible={isDeleteConfirmationModalOpen}
      />
      {updatingDocument && (
        <UpdateDocumentModal
          onUpdate={handleClickUpdate}
          documentId={updatingDocument.id}
          onClose={closeUpdateDocumentModal}
          documentTypeName={updatingDocument.documentType}
        />
      )}
      <UploadI94DocumentsModal
        visible={isUploadI94ModalOpen}
        onClose={() => setIsUploadI94ModalOpen(false)}
        onUploadComplete={refetchDocuments}
      />
    </div>
  );
};

export default Documents;
